import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Typography } from '@mui/material';
import ActionConfirmModal from './ActionConfirmModal';

const isValidEmail = (email) => {
    return Boolean(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
};

export default function SupplierModals({
    supplierModalOpen, setSupplierModalOpen, isSubmittingSupplier, supplierForm, setSupplierForm, handleCreateSupplier,
    editSupplierModalOpen, setEditSupplierModalOpen, isUpdatingSupplier, editSupplierForm, setEditSupplierForm, handleUpdateSupplier,
    deleteSupplierModalOpen, setDeleteSupplierModalOpen, isDeletingSupplier, handleDeleteSupplier
}) {
    const isSupplierEmailInvalid = Boolean(supplierForm.email?.trim() && !isValidEmail(supplierForm.email));
    const isSupplierPhoneInvalid = Boolean(supplierForm.phone && supplierForm.phone.length !== 10);
    const isEditEmailInvalid = Boolean(editSupplierForm.email?.trim() && !isValidEmail(editSupplierForm.email));

    return (
        <>
            {/* Create Supplier Modal */}
            <Dialog 
                open={supplierModalOpen} 
                onClose={() => !isSubmittingSupplier && setSupplierModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create New Supplier</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Company Name"
                                value={supplierForm.companyName}
                                onChange={(e) => setSupplierForm({ ...supplierForm, companyName: e.target.value })}
                                disabled={isSubmittingSupplier}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                label="Contact Person"
                                value={supplierForm.contactPerson}
                                onChange={(e) => setSupplierForm({ ...supplierForm, contactPerson: e.target.value })}
                                disabled={isSubmittingSupplier}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                label="Phone Number"
                                value={supplierForm.phone}
                                onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                disabled={isSubmittingSupplier}
                                placeholder="10-digit number"
                                error={isSupplierPhoneInvalid}
                                helperText={isSupplierPhoneInvalid ? 'Phone number must be exactly 10 digits' : ''}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                type="email"
                                label="Email Address"
                                value={supplierForm.email}
                                onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                                disabled={isSubmittingSupplier}
                                error={isSupplierEmailInvalid}
                                helperText={isSupplierEmailInvalid ? 'Please enter a valid email address (e.g. name@company.com)' : ''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required disabled={isSubmittingSupplier}>
                                <InputLabel id="supplier-category-label" required>Category</InputLabel>
                                <Select
                                    labelId="supplier-category-label"
                                    label="Category"
                                    value={supplierForm.category}
                                    onChange={(e) => setSupplierForm({ ...supplierForm, category: e.target.value })}
                                >
                                    <MenuItem value="F&B">F&B</MenuItem>
                                    <MenuItem value="Room Supplies">Room Supplies</MenuItem>
                                    <MenuItem value="Housekeeping">Housekeeping</MenuItem>
                                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                                    <MenuItem value="General">General</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required disabled={isSubmittingSupplier}>
                                <InputLabel id="supplier-payment-terms-label" required>Payment Terms</InputLabel>
                                <Select
                                    labelId="supplier-payment-terms-label"
                                    label="Payment Terms"
                                    value={supplierForm.paymentTerms}
                                    onChange={(e) => setSupplierForm({ ...supplierForm, paymentTerms: e.target.value })}
                                >
                                    <MenuItem value="Due on Receipt">Due on Receipt</MenuItem>
                                    <MenuItem value="Net 15">Net 15</MenuItem>
                                    <MenuItem value="Net 30">Net 30</MenuItem>
                                    <MenuItem value="Net 60">Net 60</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setSupplierModalOpen(false)} 
                        disabled={isSubmittingSupplier}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreateSupplier} 
                        variant="contained" 
                        disabled={
                            isSubmittingSupplier || 
                            !supplierForm.companyName?.trim() || 
                            !supplierForm.contactPerson?.trim() || 
                            !isValidEmail(supplierForm.email) || 
                            !supplierForm.phone?.trim() ||
                            supplierForm.phone.length !== 10 ||
                            !supplierForm.category || 
                            !supplierForm.paymentTerms
                        }
                        startIcon={isSubmittingSupplier ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isSubmittingSupplier ? 'Creating...' : 'Create Supplier'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Supplier Modal */}
            <Dialog 
                open={editSupplierModalOpen} 
                onClose={() => !isUpdatingSupplier && setEditSupplierModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Supplier Contact</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Contact Person"
                                value={editSupplierForm.contactPerson}
                                onChange={(e) => setEditSupplierForm({ ...editSupplierForm, contactPerson: e.target.value })}
                                disabled={isUpdatingSupplier}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                type="email"
                                label="Email Address"
                                value={editSupplierForm.email}
                                onChange={(e) => setEditSupplierForm({ ...editSupplierForm, email: e.target.value })}
                                disabled={isUpdatingSupplier}
                                error={isEditEmailInvalid}
                                helperText={isEditEmailInvalid ? 'Please enter a valid email address (e.g. name@company.com)' : ''}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setEditSupplierModalOpen(false)} 
                        disabled={isUpdatingSupplier}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateSupplier} 
                        variant="contained" 
                        disabled={
                            isUpdatingSupplier || 
                            !editSupplierForm.contactPerson?.trim() || 
                            !isValidEmail(editSupplierForm.email)
                        }
                        startIcon={isUpdatingSupplier ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isUpdatingSupplier ? 'Updating...' : 'Update Supplier'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ActionConfirmModal
                open={deleteSupplierModalOpen}
                onClose={() => setDeleteSupplierModalOpen(false)}
                title="Delete Supplier Profile"
                message="Warning: Are you sure you want to delete this supplier profile? This will permanently remove their records from the active vendor directory registry. This action cannot be undone."
                onConfirm={handleDeleteSupplier}
                isProcessing={isDeletingSupplier}
                confirmText="Delete Supplier"
                confirmColor="error"
            />
        </>
    );
}
